// SEQ-192: the rules for the phone navigation drawer, kept free of React so
// they can be tested under deno. The drawer (isMobileOpen) is separate state
// from the desktop collapse (isCollapsed): the hamburger must only ever move
// the drawer.

export interface SidebarState {
  isCollapsed: boolean;
  isMobileOpen: boolean;
}

// What the phone hamburger does: flip the drawer, never the desktop collapse.
export function toggleDrawer(state: SidebarState): SidebarState {
  return { isCollapsed: state.isCollapsed, isMobileOpen: !state.isMobileOpen };
}

// Escape closes an open drawer. Returns whether the key was handled.
export function handleDrawerKey(
  state: SidebarState,
  key: string,
): { state: SidebarState; handled: boolean } {
  if (key === "Escape" && state.isMobileOpen) {
    return { state: { ...state, isMobileOpen: false }, handled: true };
  }
  return { state, handled: false };
}

export function triggerLabel(isMobileOpen: boolean): string {
  return isMobileOpen ? "Close navigation" : "Open navigation";
}

export const SIDEBAR_ID = "app-sidebar";

// While the phone drawer is open the sidebar renders in full, whatever the
// saved desktop collapse says. The saved preference itself is left alone.
export function isDrawerCollapsed(isCollapsed: boolean, isMobileOpen: boolean): boolean {
  return isCollapsed && !isMobileOpen;
}

// SEQ-192: the phone drawer slides out over 280ms (transform transition on
// .sidebar in index.css). The report widget screenshots the page the moment it
// opens, so opening it in the same tick as the close captures the drawer
// mid-slide. Keep this equal to that CSS duration.
export const DRAWER_TRANSITION_MS = 280;
export const DRAWER_FALLBACK_MS = DRAWER_TRANSITION_MS + 50;

// The slice of the aside this touches, so tests can pass a fake.
export interface DrawerTransitionTarget {
  addEventListener: (
    type: "transitionend",
    listener: (e: { target: unknown; propertyName: string }) => void,
  ) => void;
  removeEventListener: (
    type: "transitionend",
    listener: (e: { target: unknown; propertyName: string }) => void,
  ) => void;
}

// Runs `open` once the drawer is gone. Drawer not open (desktop): open at once.
// Drawer open: close it, then open from the aside's own transform transitionend,
// or from a fallback timer when no transition fires (reduced motion, widths
// where the drawer does not animate). Returns a cancel function.
export function openAfterDrawerCloses(deps: {
  drawerOpen: boolean;
  closeDrawer: () => void;
  aside: DrawerTransitionTarget | null;
  open: () => void;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (id: unknown) => void;
}): () => void {
  const { drawerOpen, closeDrawer, aside, open } = deps;
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((id) => clearTimeout(id as ReturnType<typeof setTimeout>));
  if (!drawerOpen) {
    closeDrawer();
    open();
    return () => {};
  }
  let settled = false;
  let timer: unknown = null;
  const onEnd = (e: { target: unknown; propertyName: string }) => {
    if (e.target !== aside || e.propertyName !== "transform") return;
    finish();
  };
  function cleanup() {
    settled = true;
    if (timer !== null) clearTimer(timer);
    aside?.removeEventListener("transitionend", onEnd);
  }
  function finish() {
    if (settled) return;
    cleanup();
    open();
  }
  aside?.addEventListener("transitionend", onEnd);
  timer = setTimer(finish, DRAWER_FALLBACK_MS);
  closeDrawer();
  return () => {
    if (!settled) cleanup();
  };
}
